import type { IssueConsequence, OnboardingValidationIssue } from "@/api/onboarding-api.types"

export type IssueRecordKind = "worker" | "organization" | "other"
export type IssueFixLocation = OnboardingValidationIssue["fixLocation"]

/** Names the drawer already knows, used when the server named no record for an issue. */
export interface IssueRecordContext {
  workerName: string
  organizationName: string | null
}

/** The issues of one record that are corrected in the same place. */
export interface IssueFixGroup {
  fixLocation: IssueFixLocation
  /** The heaviest consequence in the group, which is the one the reviewer is agreeing to. */
  consequence: IssueConsequence
  issues: OnboardingValidationIssue[]
}

/** One record the submit would create, with everything the preflight said about it. */
export interface IssueRecord {
  key: string
  kind: IssueRecordKind
  name: string
  /** The ATS id of the record, when the server named one. */
  atsId: string | null
  blocking: OnboardingValidationIssue[]
  fixes: IssueFixGroup[]
  count: number
}

// The server names a record as "worker <id> — <name>" or "supplier <id> — <name>"; the name is optional.
const FIX_RECORD = /^(worker|supplier) (\d+)(?: — (.+))?$/

// A replaced value ranks lowest among the real consequences: its cost is already stated on its own line, as
// what it will be saved as. An unchecked organization ranks below them all, since it asserts nothing.
const WEIGHT: Record<IssueConsequence, number> = {
  NotChecked: 0,
  ValueReplaced: 1,
  ValueNotStored: 2,
  RecordIncomplete: 3,
  RecordNotCreated: 4,
}

function kindOf(field: string): IssueRecordKind {
  if (field === "worker" || field.startsWith("worker.")) return "worker"
  if (field === "workerOrganization" || field.startsWith("workerOrganization.")) return "organization"
  return "other"
}

/**
 * Groups the preflight's issues by the record they belong to, then by where each value is corrected.
 *
 * Records keep the server's order, which reports the worker before the organization. Blocking issues sit
 * apart from the rest because nothing about them can be accepted.
 */
export function groupIssuesByRecord(
  issues: OnboardingValidationIssue[],
  context: IssueRecordContext,
): IssueRecord[] {
  const records = new Map<string, IssueRecord>()

  issues.forEach((issue) => {
    const kind = kindOf(issue.field)
    const key = kind === "other" ? `other:${issue.field}` : kind
    const named = issue.fixRecord ? FIX_RECORD.exec(issue.fixRecord) : null
    let record = records.get(key)

    if (!record) {
      const fallback = kind === "worker"
        ? context.workerName
        : kind === "organization"
          ? context.organizationName ?? ""
          : issue.field
      record = {
        key,
        kind,
        name: named?.[3] ?? fallback,
        atsId: named?.[2] ?? null,
        blocking: [],
        fixes: [],
        count: 0,
      }
      records.set(key, record)
    } else if (!record.atsId && named) {
      record.atsId = named[2]
      if (named[3]) record.name = named[3]
    }

    record.count += 1

    if (issue.blocking) {
      record.blocking.push(issue)
      return
    }

    let group = record.fixes.find((f) => f.fixLocation === issue.fixLocation)
    if (!group) {
      group = { fixLocation: issue.fixLocation, consequence: issue.consequence, issues: [] }
      record.fixes.push(group)
    }
    group.issues.push(issue)
    if (WEIGHT[issue.consequence] > WEIGHT[group.consequence]) group.consequence = issue.consequence
  })

  return [...records.values()]
}
